import { WorkspaceBreadcrumbs } from '@/features/workspaces/components/workspace-breadcrumbs';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderChatButton, HeaderBackButton } from '@/ui/components/header';
import { useTranslations } from 'use-intl';
import { scopeAgentStatusQuery } from '@/features/scope-breakdown/queries';
import { workspaceQuery } from '@/features/workspaces/queries';
import { sheetSearchSchema } from '@/features/sheets/constants';
import { ScopeBreakdownPage } from '@/features/scope-breakdown/components/scope-breakdown-page';
import { artifactQuery } from '@/features/artifacts/queries';
import { sheetQuery } from '@/features/sheets/queries';
import { shownSheetId } from '@/features/scope-breakdown/utils';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/_layout/(main)/scope-breakdown/',
)({
  staticData: { chatSurface: 'kg_work_package' },
  component: RouteComponent,
  loaderDeps: ({ search }) => ({ sheetId: search.sheetId, sheetRevision: search.sheetRevision }),
  loader: async ({ context, params, deps }) => {
    const workspace = await context.queryClient.query({
      ...workspaceQuery(params.workspaceId),
      staleTime: 'static',
    });
    const status = await context.queryClient.query({
      ...scopeAgentStatusQuery(params.workspaceId, workspace.type),
      staleTime: 'static',
    });
    const sheetId = deps.sheetId ?? shownSheetId(status);
    if (sheetId) {
      try {
        const artifact = await context.queryClient.query({
          ...artifactQuery(sheetId),
          staleTime: 'static',
        });
        if (artifact.kind !== 'sheet' || artifact.workspaceId !== params.workspaceId)
          throw notFound();
        if (deps.sheetRevision !== undefined)
          await context.queryClient.query({
            ...sheetQuery(sheetId, deps.sheetRevision),
            staleTime: 'static',
          });
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) throw notFound();
        throw error;
      }
    }
    return status;
  },
  validateSearch: sheetSearchSchema,
});

function RouteComponent() {
  const tFeature = useTranslations('features.scopeBreakdown');
  const { workspaceId, projectId } = Route.useParams();

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId"
          params={{ projectId, workspaceId }}
        />
        <WorkspaceBreadcrumbs>
          <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
        </WorkspaceBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <ScopeBreakdownPage />
    </>
  );
}
